import dayjs from 'dayjs'
import { Briefcase, Building2, DollarSign, MapPin, Save } from "lucide-react"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import Swal from 'sweetalert2'
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { Textarea } from "../../components/ui/textarea"
import { Domain, Job, JobStatusEnum } from "../../swagger"
import { adminJobApi, domainApi } from "../../utils/configuration"

export default function AddJobComponent() {
  const [domains, setDomains] = useState<Domain[]>([])
  // const [, setIsLoading] = useState(false)
  
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<Job>()

  // Watch form values for preview and dependent selects
  const watchedValues = watch()
  const selectedDomainId = watch("domainId")
  const selectedCategoryId = watch("categoryId")

  // Get selected domain and category for dependent dropdowns
  const selectedDomain = domains.find((d) => d.id === selectedDomainId)
  const selectedCategory = selectedDomain?.Categories?.find((c) => c.id === selectedCategoryId)

  // API call to fetch domains
  const fetchDomains = async () => {
    try {
      // setIsLoading(true)
      const domains = await domainApi.getAllDomains()
      setDomains(domains)
    } catch (error) {
      console.error("Error fetching domains:", error)
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to load domains. Please refresh the page.',
        confirmButtonColor: '#4f46e5'
      })
    } finally {
      // setIsLoading(false)
    }
  }

  // API call to create job post
  const createJobPost = async (data: Job) => {
    try {
      const response = await adminJobApi.adminAddJob({
        job: { 
          ...data, 
          status: JobStatusEnum.Draft,
          validity: dayjs().add(30, 'D').toDate()
        }
      })
      
      return { success: true, data: response }
    } catch (error) {
      console.error("Error creating job post:", error)
      throw error
    }
  }

  useEffect(() => {
    fetchDomains()
  }, [])

  // Reset dependent fields when parent changes
  useEffect(() => {
    setValue("categoryId", undefined)
    setValue("subCategoryId", undefined)
  }, [selectedDomainId, setValue])

  useEffect(() => {
    setValue("subCategoryId", undefined)
  }, [selectedCategoryId, setValue])

  const handleSelectChange = (field: keyof Job, value: string) => {
    const numberFields: (keyof Job)[] = ["domainId", "categoryId", "subCategoryId"];
    setValue(field, numberFields.includes(field) ? Number(value) : value);
  };

  const handleSaveDraft = async (data: Job) => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: 'Save as Draft?',
      text: 'This will save your job post as draft. You can edit and publish it later.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Save!',
      cancelButtonText: 'Cancel'
    })

    if (!result.isConfirmed) return

    try {
      // Show loading
      Swal.fire({
        title: 'Saving Draft...',
        text: 'Please wait while we save your job post.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      const response = await createJobPost(data)
      
      // Success message
      await Swal.fire({
        icon: 'success',
        title: 'Draft Saved!',
        text: 'Your job post has been saved as draft successfully!',
        confirmButtonColor: '#4f46e5'
      })
      
      // Navigate to view job page
      navigate(`/admin/view-job/${response.data.id}`)
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to save draft. Please try again.',
        confirmButtonColor: '#4f46e5'
      })
    }
  }

  const handleCancel = async () => {
    const result = await Swal.fire({
      title: 'Discard Changes?',
      text: 'Are you sure you want to leave? All unsaved changes will be lost.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Discard',
      cancelButtonText: 'Keep Editing'
    })

    if (result.isConfirmed) {
      navigate('/jobs')
    }
  }

  const onSubmit = (data: Job) => {
    handleSaveDraft(data)
  }

  return (
    <div className="lg:pl-64 pt-20 w-full mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Job Post</h1>
          <p className="text-gray-600 mt-1">Post a new job opportunity</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit(onSubmit)} 
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            disabled={isSubmitting}
          >
            <Save className="h-4 w-4 mr-2" />
            {isSubmitting ? "Saving..." : "Save Draft"}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Briefcase className="h-5 w-5 mr-2 text-indigo-600" />
                  Job Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Job Title <span className='text-red-500'>*</span></Label>
                  <Input
                    id="title"
                    {...register("title", { required: "Job title is required" })}
                    placeholder="e.g. Senior Full Stack Developer"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="jobType">Job Type <span className='text-red-500'>*</span></Label>
                    <Select 
                      value={watchedValues.type} 
                      onValueChange={(value) => handleSelectChange("type", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select job type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FULLTIME">Full-time</SelectItem>
                        <SelectItem value="PARTIME">Part-time</SelectItem>
                        <SelectItem value="CONTRACT">Contract</SelectItem>
                        <SelectItem value="FREELANCE">Freelance</SelectItem>
                        <SelectItem value="INTERNSHIP">Internship</SelectItem>
                      </SelectContent>
                    </Select>
                    {!watchedValues.type && (
                      <p className="text-red-500 text-sm mt-1">Job type is required</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="experienceLevel">Experience Level <span className='text-red-500'>*</span></Label>
                    <Select
                      value={watchedValues.experienceLevel}
                      onValueChange={(value) => handleSelectChange("experienceLevel", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select experience level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ENTRY">Entry Level (0-2 years)</SelectItem>
                        <SelectItem value="MID">Mid Level (2-5 years)</SelectItem>
                        <SelectItem value="SENIOR">Senior Level (5-8 years)</SelectItem>
                        <SelectItem value="LEAD">Lead Level (8+ years)</SelectItem>
                        <SelectItem value="EXECUTIVE">Executive Level</SelectItem>
                      </SelectContent>
                    </Select>
                    {!watchedValues.experienceLevel && (
                      <p className="text-red-500 text-sm mt-1">Experience level is required</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="location">Location <span className='text-red-500'>*</span></Label>
                    <Input
                      id="location"
                      {...register("location", { required: "Location is required" })}
                      placeholder="e.g. New York, NY or Remote"
                    />
                    {errors.location && (
                      <p className="text-red-500 text-sm mt-1">{errors.location.message}</p>
                    )}
                  </div>

                 <div>
                  <Label>Salary Range <span className='text-red-500'>*</span></Label>
                  <div className="flex items-center space-x-2">
                    <Select
                      value={"INR"}
                      disabled
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">$</SelectItem>
                        <SelectItem value="EUR">€</SelectItem>
                        <SelectItem value="GBP">£</SelectItem>
                        <SelectItem value="INR">₹</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      placeholder="Min"
                      {...register("salaryStart", { required: "Min. salary is required" })}
                      className="flex-1"
                    />
                    <span className="text-gray-500">to</span>
                    <Input
                      type="number"
                      placeholder="Max"
                      {...register("salaryEnd", { required: "Max. salary is required" })}
                      className="flex-1"
                    />
                  </div>
                  {(errors.salaryStart || errors.salaryEnd) && (
                    <p className="text-red-500 text-sm mt-1">Both salary range values are required</p>
                  )}
                </div>
                </div>
              </CardContent>
            </Card>

            {/* Category Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Job Category</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="domain">Domain <span className='text-red-500'>*</span></Label>
                    <Select 
                      value={watchedValues.domainId?.toString() ?? ''} 
                      onValueChange={(value) => handleSelectChange("domainId", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select domain" />
                      </SelectTrigger>
                      <SelectContent>
                        {domains.map((domain) => (
                          <SelectItem key={domain.id} value={domain.id?.toString() ?? ''}>
                            {domain.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!watchedValues.domainId && (
                      <p className="text-red-500 text-sm mt-1">Domain is required</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="category">Category <span className='text-red-500'>*</span></Label>
                    <Select
                      value={watchedValues.categoryId?.toString()}
                      onValueChange={(value) => handleSelectChange("categoryId", value)}
                      disabled={!watchedValues.domainId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedDomain?.Categories?.map((category) => (
                          <SelectItem key={category.id} value={category.id?.toString() ?? ''}>
                            {category.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {watchedValues.domainId && !watchedValues.categoryId && (
                      <p className="text-red-500 text-sm mt-1">Category is required</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="subcategory">Subcategory <span className='text-red-500'>*</span></Label>
                    <Select
                      value={watchedValues.subCategoryId?.toString() ?? ''}
                      onValueChange={(value) => handleSelectChange("subCategoryId", value)}
                      disabled={!watchedValues.categoryId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select subcategory" />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedCategory?.SubCategories?.map((subcategory) => (
                          <SelectItem key={subcategory.id} value={subcategory.id?.toString() ?? ''}>
                            {subcategory.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Job Description */}
            <Card>
              <CardHeader>
                <CardTitle>Job Description</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="jobDescription">Job Description <span className='text-red-500'>*</span></Label>
                  <Textarea
                    id="jobDescription"
                    {...register("description", { required: "Job description is required" })}
                    placeholder="Describe the role, responsibilities, and what the candidate will be doing..."
                    rows={6}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="requirements">Requirements <span className='text-red-500'>*</span></Label>
                  <Textarea
                    id="requirements"
                    {...register("requirements", { required: "Requirements are required" })}
                    placeholder="List the required skills, experience, and qualifications..."
                    rows={4}
                  />
                  {errors.requirements && (
                    <p className="text-red-500 text-sm mt-1">{errors.requirements.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="benefits">Benefits & Perks</Label>
                  <Textarea
                    id="benefits"
                    {...register("benefits")}
                    placeholder="Describe the benefits, perks, and what makes this opportunity great..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Company Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building2 className="h-5 w-5 mr-2 text-indigo-600" />
                  Company
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="company">Company Name <span className='text-red-500'>*</span></Label>
                  <Input
                    id="company"
                    {...register("companyName", { required: "Company name is required" })}
                    placeholder="e.g. TechCorp Inc."
                  />
                  {errors.companyName && (
                    <p className="text-red-500 text-sm mt-1">{errors.companyName.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="companyDescription">Company Description <span className='text-red-500'>*</span></Label>
                  <Textarea
                    id="companyDescription"
                    {...register("companyDesc", { required: "Company description is required" })}
                    placeholder="Brief description of the company..."
                    rows={4}
                  />
                  {errors.companyDesc && (
                    <p className="text-red-500 text-sm mt-1">{errors.companyDesc.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="companyEmail">Company HR Email <span className='text-red-500'>*</span></Label>
                  <Input
                    id="companyEmail"
                    type='email'
                    {...register("companyEmail", { required: "Company HR email is required" })}
                    placeholder="e.g. TechCorp Inc."
                  />
                  {errors.companyEmail && (
                    <p className="text-red-500 text-sm mt-1">{errors.companyEmail.message}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Preview Card */}
            <Card>
              <CardHeader>
                <CardTitle>Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium">Title:</span>
                    <p className="text-gray-600">{watchedValues.title || "Not specified"}</p>
                  </div>
                  <div>
                    <span className="font-medium">Company:</span>
                    <p className="text-gray-600">{watchedValues.companyName || "Not specified"}</p>
                  </div>
                  <div>
                    <span className="font-medium">Location:</span>
                    <p className="text-gray-600 flex items-center">
                      <MapPin className="h-3 w-3 mr-1" />
                      {watchedValues.location || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium">Salary:</span>
                    <p className="text-gray-600 flex items-center">
                      <DollarSign className="h-3 w-3 mr-1" />
                      {watchedValues.salaryStart && watchedValues.salaryEnd 
                        ? `₹${watchedValues.salaryStart} - ₹${watchedValues.salaryEnd}`
                        : "Not specified"
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  )
}